import { observable, action, computed } from 'mobx'
import {
  API,
  ILocation,
  IListInventoryAuditResponse,
  IInventoryAuditFilterRequest,
  AuditStatus,
} from '@getgreenline/homi-shared'
import { parseErrorMsg } from '../../../../../utilities/helpers'

export enum statusOptions {
  PENDING = 'Pending',
  CANCELLED = 'Cancelled',
  COMPLETED = 'Completed',
}

export class AuditFilterStore {
  @observable
  companyId: number

  @observable
  locations: ILocation[] = []

  @observable
  filter: IInventoryAuditFilterRequest = {}

  @observable
  paginationLimit = 20

  @observable
  isLoading = false

  @observable
  paginatedAudits?: IListInventoryAuditResponse

  @observable
  searchText?: string

  @observable
  searchId?: string

  @observable
  filteredStatus?: AuditStatus

  @observable
  filteredLocationIds?: number[]

  @observable
  isFiltered = false

  @computed
  get currentPage(): number {
    if (!this.paginatedAudits || this.paginatedAudits.limit === 0) return 0
    return this.paginatedAudits.offset / this.paginatedAudits.limit
  }

  @computed
  get pageCount(): number {
    if (!this.paginatedAudits || this.paginatedAudits.limit === 0) return 0
    return Math.ceil(this.paginatedAudits.total / this.paginatedAudits.limit)
  }

  constructor(companyId: number) {
    this.companyId = companyId
  }

  @action
  setLocations = (locations: ILocation[]) => {
    this.locations = locations
  }

  @action
  setFilter = (filter: IInventoryAuditFilterRequest) => {
    this.filter = filter
  }

  @action
  loadAudits = async (offset = 0) => {
    try {
      this.isLoading = true
      const newFilter: IInventoryAuditFilterRequest = {
        ...this.filter,
        offset,
        limit: this.paginationLimit,
      }

      this.setFilter(newFilter)

      const paginatedAudits = await API.getInventoryAudits(this.companyId, newFilter)

      this.paginatedAudits = paginatedAudits
    } catch (error) {
      parseErrorMsg(error || 'An error occurred while loading audits')
    } finally {
      this.isLoading = false
    }
  }

  @action
  setSearchText = (searchText?: string) => {
    this.searchText = searchText
  }

  @action
  setSearchId = (searchId?: string) => {
    this.searchId = searchId
  }

  @action
  setFilteredStatus = (filteredStatus?: AuditStatus) => {
    this.filteredStatus = filteredStatus
  }

  @action
  setFilteredLocationIds = (locationIds?: number[]) => {
    const filteredLocationIds = (locationIds || []).length > 0 ? locationIds : undefined
    this.filteredLocationIds = filteredLocationIds
  }

  @action
  applyFilter = async () => {
    const newFilter = {
      title: this.convertAndTrimText(this.searchText),
      id: this.convertAndTrimText(this.searchId),
      status: this.filteredStatus,
      locationIds: this.filteredLocationIds,
    }

    if (this.searchText || this.searchId || this.filteredStatus || this.filteredLocationIds) {
      this.isFiltered = true
    } else {
      this.isFiltered = false
    }
    this.setFilter(newFilter)
    await this.loadAudits(0)
  }

  @action
  resetFilter = async () => {
    this.setSearchText()
    this.setFilteredLocationIds()
    this.setFilteredStatus()
    this.setSearchId()
    this.isFiltered = false
    await this.applyFilter()
  }

  convertAndTrimText = (text?: string) => (text ? text.trim().toLowerCase() : undefined)
}
